import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ogImage } from '../../lib/ogImage';
import { Instagram, ChevronRight } from 'lucide-react';
import BlogLayout from '../../components/BlogLayout';

const SLUG = 'tattoo-studio-near-exarchia';
const URL = `https://210tattoo.gr/blog/${SLUG}`;

export default function TattooStudioNearExarchia() {
  useEffect(() => {
    const inject = (id: string, s: object) => {
      let tag = document.getElementById(id);
      if (!tag) { tag = document.createElement('script'); tag.id = id; (tag as HTMLScriptElement).type = 'application/ld+json'; document.head.appendChild(tag); }
      tag.textContent = JSON.stringify(s);
    };
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "Τατουάζ κοντά στα Εξάρχεια: πιο κοντά απ' όσο νομίζεις",
      "description": "Ψάχνεις τατουάζ κοντά στα Εξάρχεια; Το 210 Tattoo Athens είναι λίγα λεπτά με τα πόδια. Custom σχέδια, fine line και blackwork τατουάζ Αθήνα από έμπειρους artists.",
      "image": "https://210tattoo.gr/og-image.png",
      "datePublished": "2026-07-17",
      "dateModified": "2026-07-17",
      "author": { "@type": "Organization", "name": "210 Tattoo Athens", "url": "https://210tattoo.gr" },
      "publisher": { "@id": "https://210tattoo.gr/#tattooparlor" },
      "url": URL,
      "inLanguage": "el"
    };
    inject('schema-near-exarchia-article', articleSchema);
    return () => {
      document.getElementById('schema-near-exarchia-article')?.remove();
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Τατουάζ Στούντιο κοντά στα Εξάρχεια | 210 Tattoo Athens</title>
        <meta name="description" content="Ψάχνεις τατουάζ κοντά στα Εξάρχεια; Το 210 Tattoo Athens είναι λίγα λεπτά με τα πόδια. Custom σχέδια, fine line και blackwork τατουάζ Αθήνα από έμπειρους artists." />
        <link rel="canonical" href={URL} />
        <link rel="alternate" hrefLang="el" href={URL} />
        <link rel="alternate" hrefLang="x-default" href={URL} />
        <meta property="og:title" content="Τατουάζ Στούντιο κοντά στα Εξάρχεια | 210 Tattoo Athens" />
        <meta property="og:description" content="Το 210 Tattoo Athens είναι λίγα λεπτά με τα πόδια από τα Εξάρχεια. Custom, fine line και blackwork τατουάζ Αθήνα." />
        <meta property="og:url" content={URL} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={ogImage(SLUG)} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Τατουάζ Στούντιο κοντά στα Εξάρχεια | 210 Tattoo Athens" />
        <meta name="twitter:description" content="Λίγα λεπτά με τα πόδια από την πλατεία Εξαρχείων, στη Γκύζη." />
        <meta name="twitter:image" content={ogImage(SLUG)} />
      </Helmet>

      <BlogLayout>
        {/* Hero */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-950/20 via-black to-black pointer-events-none" />
          <div className="max-w-4xl mx-auto relative z-10">
            <div className="flex items-center gap-2 text-pink-500 text-sm font-medium mb-6">
              <a href="/" className="hover:text-pink-400 transition-colors">Home</a>
              <ChevronRight size={14} />
              <span>Τατουάζ κοντά στα Εξάρχεια</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-pink-500 via-pink-400 to-pink-300 bg-clip-text text-transparent">Τατουάζ κοντά στα Εξάρχεια:</span>
              <br />
              <span className="text-white">πιο κοντά απ' όσο νομίζεις</span>
            </h1>
            <p className="text-gray-500 text-xs mb-4 tracking-widest uppercase">17 Ιουλίου 2026</p>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed max-w-2xl">
              Αν μένεις γύρω από την πλατεία Εξαρχείων, ή περνάς συχνά από εκεί για καφέ, δισκάδικα και βόλτα, το 210 Tattoo Athens είναι πιο κοντά σου απ' όσο ίσως έχεις σκεφτεί. Ψάχνοντας για τατουάζ στην Αθήνα, αρκετός κόσμος από τα Εξάρχεια καταλήγει σε μας ακριβώς επειδή το studio μας βρίσκεται σε περπατήσιμη απόσταση, στη γειτονική Γκύζη.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Κλείσε Ραντεβού
            </a>
          </div>
        </section>

        {/* Distance */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πόσο μακριά είναι <span className="text-pink-500">πραγματικά</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed">
              Από την πλατεία Εξαρχείων μέχρι την Αναστασίου Γενναδίου 34 είναι μια βόλτα περίπου 10 με 12 λεπτών με τα πόδια, ανηφορικά προς τη Γκύζη, περνώντας κοντά από τον λόφο του Στρέφη. Αν δεν έχεις όρεξη για ανηφόρα, μία ή δύο στάσεις λεωφορείου σε φέρνουν στην πόρτα μας εξίσου γρήγορα. Δεν χρειάζεται μετρό, δεν χρειάζεται ταξί, μόνο μια σύντομη βόλτα σε μια περιοχή που πιθανότατα ήδη γνωρίζεις καλά.
            </p>
          </div>
        </section>

        {/* What locals ask for */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Τι συνήθως ζητούν <span className="text-pink-500">οι πελάτες από τα Εξάρχεια</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Τα Εξάρχεια έχουν μακρά σχέση με την τέχνη και την υποκουλτούρα, και αυτό φαίνεται στις ιδέες που φέρνει ο κόσμος στο consultation. Βλέπουμε αρκετά αιτήματα για <a href="/blackwork-tattoo-athens" className="text-pink-400 hover:text-pink-300 underline underline-offset-4">blackwork τατουάζ</a> με έντονη γεωμετρία, για <a href="/dotwork-tattoo-athens" className="text-pink-400 hover:text-pink-300 underline underline-offset-4">dotwork</a> με λεπτομερή υφή και σκιάσεις, αλλά και για πιο ήσυχα, ατομικά σχέδια. Αν έχεις μια ιδέα που δεν βρίσκεις έτοιμη πουθενά, το <a href="/custom-tattoo-athens" className="text-pink-400 hover:text-pink-300 underline underline-offset-4">custom τατουάζ</a> είναι συνήθως η σωστή κατεύθυνση: σχεδιάζουμε από το μηδέν, γύρω από αυτό που θες να πεις με το σχέδιο, όχι από κάποιο flash σχέδιο του τοίχου. Αρκετοί πελάτες από τη γειτονιά προτιμούν επίσης κάτι πιο διακριτικό, οπότε το <a href="/fine-line-tattoo-athens" className="text-pink-400 hover:text-pink-300 underline underline-offset-4">fine line τατουάζ</a> είναι συχνή επιλογή, ειδικά για πρώτο τατουάζ ή για κάτι μικρό και καθαρό που θέλεις να κρατήσεις simple.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed">
              Πολλοί φοιτητές από το κοντινό Πολυτεχνείο περνούν επίσης από το studio, συνήθως με πιο πειραματικές ιδέες ή με σχέδια που έχουν σχεδιάσει οι ίδιοι και θέλουν να δουν πώς θα δουλέψουν στο δέρμα. Σε αυτές τις περιπτώσεις το consultation παίζει μεγάλο ρόλο, γιατί προσαρμόζουμε το σχέδιο στο σημείο του σώματος πριν καν πιάσουμε τη μηχανή.
            </p>
          </div>
        </section>

        {/* Hours */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Ώρες που ταιριάζουν <span className="text-pink-500">στον ρυθμό της γειτονιάς</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Ξέρουμε ότι τα Εξάρχεια δεν είναι γειτονιά πρωινών ραντεβού. Το studio ανοίγει στις 11:00 τις καθημερινές, ώρα που δουλεύει καλά για όσους έχουν νυχτερινό πρόγραμμα ή δεν βιάζονται να βγουν από το σπίτι νωρίς. Δουλεύουμε Δευτέρα έως Παρασκευή 11:00-20:00 και Σάββατο 11:00-19:00, οπότε υπάρχει αρκετό περιθώριο να βρεις ώρα που σου ταιριάζει χωρίς να αλλάξεις όλο το πρόγραμμά σου.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed">
              Αν περπατάς προς τα εκεί μέσα από τη Θεμιστοκλέους ή τη Ζαΐμη, η διαδρομή είναι ευθεία και εύκολη να θυμηθείς, δεν χρειάζεται Google Maps μετά την πρώτη φορά.
            </p>
          </div>
        </section>

        {/* How we work */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πώς <span className="text-pink-500">δουλεύουμε</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed">
              Στο studio, στη Γκύζη, λειτουργούμε από το 2018 με σταθερές αρχές: πιστοποιημένα μελάνια, αποστειρωμένος εξοπλισμός μίας χρήσης, και σχέδια φτιαγμένα αποκλειστικά για τον κάθε πελάτη μετά από σύντομο consultation. Πριν κλείσεις ραντεβού οπουδήποτε, ρίξε μια ματιά στο άρθρο μας <Link to="/blog/tattoo-artist-athens" className="text-pink-400 hover:text-pink-300 underline underline-offset-4">πώς να διαλέξεις tattoo artist στην Αθήνα</Link>. Καλύπτει τι να προσέξεις σε portfolio, υγιεινή και επικοινωνία, ανεξάρτητα αν τελικά επιλέξεις εμάς.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Κλείσε <span className="text-pink-500">το ραντεβού σου</span></h2>
            <p className="text-gray-300 text-lg mb-8">
              Η Γκύζη και τα Εξάρχεια λειτουργούν σχεδόν σαν μία γειτονιά για όποιον περπατάει καθημερινά στην περιοχή. Δεν χρειάζεται να διασχίσεις την πόλη για να βρεις προσεγμένο τατουάζ Αθήνα, με σωστή υγιεινή και σχέδιο φτιαγμένο ειδικά για σένα. Αν έχεις ιδέα, ασαφή ή συγκεκριμένη, στείλε μας μήνυμα στο <a href="https://www.instagram.com/210tattoo/" target="_blank" rel="noopener noreferrer" className="text-pink-400 hover:text-pink-300 underline underline-offset-4">Instagram</a> και κλείσε ένα πρώτο ραντεβού για να τη συζητήσουμε.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Στείλε Μήνυμα
            </a>
          </div>
        </section>

        {/* Related articles */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8">Σχετικά <span className="text-pink-500">Άρθρα</span></h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/blog/tattoo-artist-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Πώς να Διαλέξεις Tattoo Artist στην Αθήνα</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Portfolio, υγιεινή, consultation και red flags.</p>
              </Link>
              <Link to="/blog/tatouaz-kentro-athinas" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Γιατί να Κάνεις Τατουάζ στο Κέντρο της Αθήνας</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Τι κερδίζεις επιλέγοντας στούντιο στο κέντρο.</p>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-950 border-t border-pink-600/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Υπηρεσίες <span className="text-pink-500">/ Services</span></h2>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/blackwork-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Blackwork Tattoo Athens</a>
              <a href="/dotwork-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Dotwork Tattoo Athens</a>
              <a href="/custom-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Custom Tattoo Athens</a>
              <a href="/fine-line-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Fine Line Tattoo Athens</a>
            </div>
          </div>
        </section>
      </BlogLayout>
    </>
  );
}
